// src/pages/pos/PosProductBulkUpload.js
import React, { useState } from "react";
import axios from "axios";
import { Row, Col, Card, Table, Badge, Spinner, Alert } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import productAPI from '../../services/productapi';
const SAMPLE_CSV_URL =
    "https://grace-prod-bucket.s3.ap-south-1.amazonaws.com/uploads/samples/gsm_pos_product_bulk_upload.csv";

const PosProductBulkUpload = () => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState(null); // full API response
    const [summary, setSummary] = useState(null); // result.data

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        setFile(selected || null);
        setResult(null);
        setSummary(null);
    };

    const handleDownloadSample = () => {
        // just open the sample CSV
        window.open(SAMPLE_CSV_URL, "_blank", "noopener,noreferrer");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            toast.error("Please select a CSV file before uploading.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setIsUploading(true);
            setResult(null);
            setSummary(null);

            // 🔴 Change URL to your actual API endpoint
            const resData = await productAPI.uploadPosProductExcel(formData); // <-- already data
            setResult(resData);
            if (resData.status === "SUCCESS") {
                toast.success(resData.message || "POS bulk upload processed successfully.");
                setSummary(resData.data || null);
            } else {
                toast.error(resData.message || "POS bulk upload failed.");
                setSummary(resData.data || null);
            }
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong while uploading."
            );
        } finally {
            setIsUploading(false);
        }
    };

    const renderSummary = () => {
        if (!summary) return null;

        const {
            totalRows,
            processedRows,
            newItems,
            existingItems,
            inventoryInserted,
            inventoryUpdated,
            noApiStockItems = [],
            missingBranches = [],
            errors = [],
        } = summary;

        return (
            <Card className="mt-4">
                <Card.Header>
                    <strong>Upload Summary</strong>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={4}>
                            <p>
                                <strong>Total Rows:</strong> {totalRows}
                            </p>
                            <p>
                                <strong>Processed Rows:</strong> {processedRows}
                            </p>
                        </Col>
                        <Col md={4}>
                            <p>
                                <strong>New Items:</strong>{" "}
                                <Badge bg="success">{newItems}</Badge>
                            </p>
                            <p>
                                <strong>Existing Items:</strong>{" "}
                                <Badge bg="secondary">{existingItems}</Badge>
                            </p>
                        </Col>
                        <Col md={4}>
                            <p>
                                <strong>Inventory Inserted:</strong>{" "}
                                <Badge bg="info">{inventoryInserted}</Badge>
                            </p>
                            <p>
                                <strong>Inventory Updated:</strong>{" "}
                                <Badge bg="warning">{inventoryUpdated}</Badge>
                            </p>
                        </Col>
                    </Row>

                    {noApiStockItems.length > 0 && (
                        <>
                            <h6>No RayMedi Stock Data for Item Codes</h6>
                            <Alert variant="light">
                                {noApiStockItems.join(", ")}
                            </Alert>
                        </>
                    )}

                    {missingBranches.length > 0 && (
                        <>
                            <h6 className="mt-3">Missing Branch Mappings</h6>
                            <Table bordered size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Item Code</th>
                                        <th>Outlet ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingBranches.map((row, index) => (
                                        <tr key={`${row.itemCode}-${row.outletId}-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>{row.itemCode}</td>
                                            <td>{row.outletId}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}

                    {errors.length > 0 && (
                        <>
                            <h6 className="mt-3 text-danger">Row Errors</h6>
                            <Table bordered size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Product Name</th>
                                        <th>Item Code</th>
                                        <th>Category Slug</th>
                                        <th>Brand Slug</th>
                                        <th>Error Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {errors.map((err, errIndex) => {
                                        // rowData may be an array (new response) or a single object (old response)
                                        const rows = Array.isArray(err.rowData)
                                            ? err.rowData
                                            : [err.rowData || {}];

                                        return rows.map((row, rowIndex) => (
                                            <tr key={`${errIndex}-${rowIndex}`}>
                                                <td>{errIndex + 1}</td>
                                                <td>{row["Product Name"] || "-"}</td>
                                                <td>{row["Item Code"] || "-"}</td>
                                                <td>{row["Category Slug"] || "-"}</td>
                                                <td>{row["Brand Slug"] || "-"}</td>
                                                <td className="text-danger">
                                                    {err.message || "Unknown error"}
                                                </td>
                                            </tr>
                                        ));
                                    })}
                                </tbody>
                            </Table>
                        </>
                    )}


                    {!noApiStockItems.length &&
                        !missingBranches.length &&
                        !errors.length && (
                            <Alert variant="success" className="mt-3 mb-0">
                                All rows processed successfully with no issues.
                            </Alert>
                        )}
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container mt-4 mb-5">
            <ToastContainer />
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-0">POS Product Bulk Upload</h4>
                        <small className="text-muted">
                            Fix the below rows in your CSV or database (pro_id default) and re-upload.
                        </small>
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadSample}
                    >
                        Download Sample CSV
                    </button>
                </Card.Header>

                <Card.Body>
                    <form onSubmit={handleSubmit}>
                        <Row className="align-items-center">
                            <Col md={6} className="mb-3">
                                <label className="form-label">Upload CSV File</label>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="form-control"
                                    onChange={handleFileChange}
                                />
                                <div className="form-text">
                                    Max 100 rows per upload (for performance).
                                </div>
                            </Col>
                            <Col
                                md={6}
                                className="mb-3 d-flex align-items-end justify-content-end"
                            >
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={isUploading || !file}
                                >
                                    {isUploading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Processing...
                                        </>
                                    ) : (
                                        "Upload & Process"
                                    )}
                                </button>
                            </Col>
                        </Row>
                    </form>

                    {result && (
                        <Alert
                            variant={result.status === "SUCCESS" ? "success" : "danger"}
                            className="mt-3"
                        >
                            <strong>{result.status}</strong> – {result.message}
                        </Alert>
                    )}

                    {renderSummary()}
                </Card.Body>
            </Card>
        </div>
    );
};

export default PosProductBulkUpload;
